import { estEnRetrait } from "./badges";
import type { Etape, Pari, PrerequisOuvert } from "./contract";
import { joursDepuis, nomCourt } from "./format";

/**
 * Le classement de l'accueil : les paris vivants devant, ceux qui dorment repliés derriere,
 * et les gestes ranges par ce qu'ils debloquent plutot que par leur seul age.
 *
 * Pourquoi ce fichier existe (issue #125) : la page d'origine tenait 7 paris et 4 gestes
 * d'une ligne. A 22 paris et 32 gestes, trier par anciennete met sur le meme plan un geste
 * qui bloque un build et un geste qui rouvrirait une idee parquee, que personne n'attend.
 *
 * Tout est pur et sans horloge implicite : `maintenant` est toujours passe en argument.
 *
 * **Rien n'est retrie.** L'API rend les paris par mise a jour decroissante et les gestes du
 * plus ancien au plus recent ; ces fonctions ne font que **repartir** en conservant l'ordre
 * d'arrivee. Un groupe prend donc le rang de son geste le plus ancien sans qu'aucun tri ne
 * soit ecrit ici, et il n'y a toujours qu'une seule verite sur l'ordre : celle de l'API.
 */

/** Une tranche de la liste des paris : son titre, et les etapes qu'elle ramasse. */
export type Section = {
  readonly cle: string;
  readonly titre: string;
  readonly etapes: readonly Etape[];
  /** L'etape dont la section prend la couleur, quand elle en ramasse plusieurs. */
  readonly point: Etape;
};

/**
 * Les paris vivants, du plus avance au moins avance. « A instruire » reunit les trois
 * etapes d'avant le test : elles ne meritent pas trois titres pour une poignee de paris.
 */
export const SECTIONS: readonly Section[] = [
  { cle: "live", titre: "Live", etapes: ["live"], point: "live" },
  { cle: "build", titre: "Build", etapes: ["build"], point: "build" },
  { cle: "test", titre: "En test", etapes: ["en test"], point: "en test" },
  {
    cle: "instruire",
    titre: "À instruire",
    etapes: ["grillée", "triée", "idée"],
    point: "idée",
  },
];

/** Les etapes de la barre de sortie, celles qui ne demandent plus rien. */
export const ETAPES_AU_REPOS: readonly Etape[] = ["parquée", "tuée"];

/** Les six etapes du flux, dans l'ordre ou un pari les traverse. */
export const ETAPES_DU_FLUX: readonly Etape[] = [
  "idée",
  "triée",
  "grillée",
  "en test",
  "build",
  "live",
];

/** Un pari qui dort : il reste lisible, mais il quitte la file des cartes. */
export function estAuRepos(etape: Etape): boolean {
  return estEnRetrait(etape);
}

/** Les gestes d'un meme pari, ou d'une meme famille sans pari. */
export type GroupeGestes = {
  readonly cle: string;
  /** Le nom affiche : nom court du pari, ou le libelle de la famille. */
  readonly nom: string;
  /** L'identifiant du pari, quand le groupe en porte un. */
  readonly pari: string | null;
  /** Le titre entier du pari, pour l'infobulle. */
  readonly titre: string | null;
  readonly gestes: readonly PrerequisOuvert[];
  /** Age du geste le plus ancien du groupe, en jours. Null si aucun ne porte de date. */
  readonly jours: number | null;
  /**
   * Vrai quand le retard de ce groupe merite du rouge, c'est-a-dire quand il bloque un pari
   * vivant ou le Lab lui-meme. Un geste qui rouvrirait une idee au repos n'est attendu par
   * personne : le laisser rougir ferait de la couleur un bruit de fond.
   */
  readonly urgent: boolean;
};

/** Ce que les gestes d'une meme famille debloquent. */
export type FamilleGestes = {
  readonly cle: "live" | "build" | "test" | "sans";
  readonly titre: string;
  readonly groupes: readonly GroupeGestes[];
  readonly total: number;
};

/** Les familles, dans l'ordre d'affichage. La derniere ramasse tout ce qui ne bloque rien. */
const FAMILLES: readonly { cle: FamilleGestes["cle"]; titre: string }[] = [
  { cle: "live", titre: "Bloquent une app en ligne" },
  { cle: "build", titre: "Bloquent un build" },
  { cle: "test", titre: "Bloquent un test" },
  { cle: "sans", titre: "Sans pari vivant à débloquer" },
];

/** Les groupes de la famille « sans », dans leur ordre d'affichage. */
const SANS = [
  { cle: "lab", nom: "Le Lab lui-même, hors idée", urgent: true },
  { cle: "instruire", nom: "Sur une idée à instruire", urgent: false },
  { cle: "repos", nom: "Pour rouvrir une idée au repos", urgent: false },
] as const;

type CleSans = (typeof SANS)[number]["cle"];

/**
 * Age du plus ancien d'une liste de gestes. Null quand aucun ne porte de date.
 *
 * Le parametre n'est pas un `PrerequisOuvert` mais tout ce qui porte une date : la carte
 * d'un pari s'en sert sur ses `prerequis`, qui n'ont ni pari ni cible.
 */
export function plusAncien(
  gestes: readonly { readonly depuis: string | null }[],
  maintenant: number,
): number | null {
  let vieux: number | null = null;
  for (const geste of gestes) {
    const jours = joursDepuis(geste.depuis, maintenant);
    if (jours !== null && (vieux === null || jours > vieux)) vieux = jours;
  }
  return vieux;
}

/** Ou ranger un geste : la famille, et le groupe quand la famille est « sans ». */
function ranger(
  geste: PrerequisOuvert,
  pari: Pari | undefined,
): { famille: FamilleGestes["cle"]; sans: CleSans | null } {
  // Un geste hors idee ne porte pas de pari : c'est le Lab lui-meme qui attend.
  if (geste.source === "hors-idee") return { famille: "sans", sans: "lab" };
  // Un pari que la liste ne porte pas (source inconnue, donnee incoherente) ne peut pas
  // etre declare urgent : on ne sait pas ce qu'il bloque.
  if (!pari) return { famille: "sans", sans: "instruire" };
  if (pari.etape === "live") return { famille: "live", sans: null };
  if (pari.etape === "build") return { famille: "build", sans: null };
  if (pari.etape === "en test") return { famille: "test", sans: null };
  return { famille: "sans", sans: estAuRepos(pari.etape) ? "repos" : "instruire" };
}

/**
 * Les gestes ouverts, ranges par ce qu'ils debloquent puis par pari.
 *
 * Les familles et les groupes vides ne sont pas rendus : une etiquette « Bloquent un build »
 * sans rien dessous se lit comme une panne.
 */
export function classerGestes(
  prerequis: readonly PrerequisOuvert[],
  paris: readonly Pari[],
  maintenant: number,
): readonly FamilleGestes[] {
  const parId = new Map(paris.map((pari) => [pari.id, pari]));
  // Une entree par cle de groupe, creee a la volee : le premier geste rencontre fixe le rang
  // du groupe, et l'ordre de l'API (du plus ancien au plus recent) se transmet tel quel.
  const groupes = new Map<
    string,
    {
      famille: FamilleGestes["cle"];
      groupe: Omit<GroupeGestes, "jours"> & { gestes: PrerequisOuvert[] };
    }
  >();

  for (const geste of prerequis) {
    const pari = geste.pari === null ? undefined : parId.get(geste.pari);
    const { famille, sans } = ranger(geste, pari);
    // Dans « sans », les gestes se rassemblent par famille et non par pari : ils sont peu
    // nombreux et aucun n'a de suite a donner tout de suite. La cle est prefixee pour que
    // les deux espaces de noms (familles et identifiants de pari) ne puissent pas se croiser.
    const cle = sans === null ? (geste.pari ?? "sans:pari-inconnu") : `sans:${sans}`;
    const existant = groupes.get(cle);
    if (existant) {
      existant.groupe.gestes.push(geste);
      continue;
    }
    const modele = sans === null ? null : SANS.find((entree) => entree.cle === sans);
    // Le nom court dans le rail (« Baraton »), le titre entier en infobulle : une colonne
    // de 21rem ne tient pas « Baraton, la tournee des bars organisee (bandes d'etudiants) ».
    const titre = pari?.titre ?? geste.titre;
    groupes.set(cle, {
      famille,
      groupe: {
        cle,
        nom: modele ? modele.nom : titre === null ? "Pari inconnu" : nomCourt(titre),
        pari: modele ? null : geste.pari,
        titre: modele ? null : titre,
        gestes: [geste],
        urgent: modele ? modele.urgent : true,
      },
    });
  }

  return FAMILLES.map(({ cle, titre }) => {
    const retenus = [...groupes.values()].filter((entree) => entree.famille === cle);
    // Dans « sans », l'ordre des groupes est celui de SANS (le Lab avant ce qui dort), pas
    // celui des gestes : c'est une echelle d'attention, pas une chronologie.
    if (cle === "sans") {
      const rang = (groupe: { cle: string }): number =>
        SANS.findIndex((entree) => `sans:${entree.cle}` === groupe.cle);
      retenus.sort((a, b) => rang(a.groupe) - rang(b.groupe));
    }
    const groupesRendus = retenus.map(({ groupe }) => ({
      ...groupe,
      jours: plusAncien(groupe.gestes, maintenant),
    }));
    return {
      cle,
      titre,
      groupes: groupesRendus,
      total: groupesRendus.reduce((somme, groupe) => somme + groupe.gestes.length, 0),
    };
  }).filter((famille) => famille.groupes.length > 0);
}

/** Un meme guide attendu par plusieurs paris : une decision, plusieurs deblocages. */
export type GestePartage = {
  readonly guide: string;
  /** Ce que les gestes disent tous : leur debut commun, jamais le texte de l'un d'eux. */
  readonly quoi: string;
  readonly gestes: readonly PrerequisOuvert[];
};

/**
 * Le debut commun a plusieurs textes, coupe au dernier mot entier.
 *
 * Il ne sert qu'a nommer un groupe sans mentir : afficher le texte du premier geste
 * pretendrait qu'il vaut pour les autres, et c'est faux des que les libelles different.
 */
export function debutCommun(textes: readonly string[]): string {
  let commun = textes[0] ?? "";
  for (const texte of textes.slice(1)) {
    let taille = 0;
    while (taille < commun.length && taille < texte.length && commun[taille] === texte[taille]) {
      taille += 1;
    }
    commun = commun.slice(0, taille);
  }
  // Le commun s'arrete deja sur une fin de mot partout : rien a couper. Sans ce controle,
  // « Remplir l'identite de l'editeur » et « ... de l'editeur avant le lancement » perdraient
  // un mot pour rien, et le geste partage tomberait sous le seuil de `gestesPartages`.
  const finDeMot = textes.every(
    (texte) => texte.length === commun.length || texte[commun.length] === " ",
  );
  if (finDeMot) return commun.trim();
  const espace = commun.lastIndexOf(" ");
  return (espace < 0 ? commun : commun.slice(0, espace)).trim();
}

/**
 * En dessous, le debut commun ne dit plus de quel geste il s'agit. Trois « Relire la spec
 * de ... » differentes s'arretent a « Relire la spec », qui ne designe rien : ce sont trois
 * lectures distinctes qui partagent un nom de fichier, pas un geste partage.
 */
const MINIMUM_COMMUN = 25;

/**
 * Les gestes que plusieurs paris attendent **vraiment** : meme guide, et libelles qui disent
 * assez longtemps la meme chose pour qu'un seul titre les couvre.
 *
 * Chaque pari garde **son** lien : `identite-editeur-app` vit dans trois dossiers, ce sont
 * trois fichiers, et les reunir sous une seule adresse mentirait.
 *
 * Seuls comptent les gestes de dossiers : deux gestes hors idee qui portent le meme guide
 * sont le meme fichier, donc le meme geste, pas un geste partage.
 */
export function gestesPartages(prerequis: readonly PrerequisOuvert[]): readonly GestePartage[] {
  const parGuide = new Map<string, PrerequisOuvert[]>();
  for (const geste of prerequis) {
    // `cible` nulle : le geste n'a pas de page, son libelle resterait du texte brut dans
    // l'encart, sans le nom accessible qui distingue les liens. Un prerequis porte par une
    // opportunite du scout est dans ce cas (pas de slug, donc pas de cible).
    if (geste.guide === null || geste.cible === null) continue;
    if (geste.source === "hors-idee" || geste.pari === null) continue;
    const liste = parGuide.get(geste.guide);
    if (liste) liste.push(geste);
    else parGuide.set(geste.guide, [geste]);
  }
  const partages: GestePartage[] = [];
  for (const [guide, gestes] of parGuide) {
    const paris = new Set(gestes.map((geste) => geste.pari));
    if (paris.size < 2) continue;
    const libelles = gestes.map((geste) => geste.quoi);
    const quoi = debutCommun(libelles);
    // Le seuil ne mord que si le debut commun a vraiment ete tronque : deux paris qui
    // attendent mot pour mot « Choisir le nom » ne mentent sur rien, meme en 14 caracteres.
    const tronque = libelles.some((libelle) => libelle !== quoi);
    if (tronque && quoi.length < MINIMUM_COMMUN) continue;
    partages.push({ guide, quoi, gestes });
  }
  return partages;
}
